import React, { useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import {
  Users,
  Phone,
  Mail,
  MessageSquare,
  Send,
  CalendarDays,
  PawPrint,
  Baby,
  Sparkles,
  AlertCircle,
  Info,
} from "lucide-react";

const FORMSUBMIT_ENDPOINT = "https://formsubmit.co/ajax/wilhelm.martel@orange.fr";
const PHONE = "+33664104815";
const PHONE_DISPLAY = "06 64 10 48 15";
const TOWEL_PRICE = 6;

const todayStr = () => new Date().toISOString().split("T")[0];

const ContactFormSection = () => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    checkin: "",
    checkout: "",
    guests: "",
    pet: "non",
    petCount: "",
    towels: false,
    babyCot: false,
    babyChair: false,
    name: "",
    email: "",
    phone: "",
    message: "",
    _honey: "",
  });
  const [status, setStatus] = useState("idle"); // idle | sending | error
  const [errors, setErrors] = useState({});

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({ ...prev, [name]: type === "checkbox" ? checked : value }));
  };

  const nights = useMemo(() => {
    if (!formData.checkin || !formData.checkout) return 0;
    const diff = (new Date(formData.checkout) - new Date(formData.checkin)) / 86400000;
    return diff > 0 ? Math.round(diff) : 0;
  }, [formData.checkin, formData.checkout]);

  const towelEstimate = useMemo(() => {
    if (!formData.towels || !formData.guests) return 0;
    return Number(formData.guests) * TOWEL_PRICE;
  }, [formData.towels, formData.guests]);

  const formatDate = (d) => {
    if (!d) return "";
    return new Date(d).toLocaleDateString("fr-FR", { day: "numeric", month: "long" });
  };

  const validate = () => {
    const e = {};
    if (!formData.checkin) e.checkin = "Indiquez votre date d'arrivée";
    if (!formData.checkout) e.checkout = "Indiquez votre date de départ";
    if (formData.checkin && formData.checkout && nights <= 0)
      e.checkout = "La date de départ doit être après l'arrivée";
    else if (formData.checkin && formData.checkout && nights < 2)
      e.checkout = "Séjour minimum : 2 nuits";
    if (!formData.guests) e.guests = "Indiquez le nombre de voyageurs";
    if (!formData.name.trim()) e.name = "Votre nom est requis";
    if (!formData.email.trim()) e.email = "Votre e-mail est requis";
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (formData._honey) return; // honeypot: bot détecté
    if (!validate()) return;

    setStatus("sending");

    const babyEquip = [];
    if (formData.babyCot) babyEquip.push("Lit bébé");
    if (formData.babyChair) babyEquip.push("Chaise haute");

    const payload = {
      "Nom": formData.name,
      email: formData.email,
      "Téléphone": formData.phone || "Non renseigné",
      "Arrivée": formatDate(formData.checkin),
      "Départ": formatDate(formData.checkout),
      "Nombre de nuits": String(nights),
      "Voyageurs": formData.guests,
      "Animal":
        formData.pet === "oui"
          ? "Oui" + (formData.petCount ? ` (${formData.petCount})` : "")
          : "Non",
      "Serviettes": formData.towels
        ? `Oui — ${towelEstimate} € (2 serviettes/personne, 6 €/personne pour le séjour)`
        : "Non",
      "Équipement bébé": babyEquip.length ? babyEquip.join(", ") : "Non",
      "Message": formData.message || "—",
      _subject: "Nouvelle demande de séjour — Cœurs de Normands",
      _template: "table",
      _captcha: "false",
    };

    try {
      const res = await fetch(FORMSUBMIT_ENDPOINT, {
        method: "POST",
        headers: { "Content-Type": "application/json", Accept: "application/json" },
        body: JSON.stringify(payload),
      });
      const data = await res.json();
      if (res.ok && (data.success === "true" || data.success === true)) {
        navigate("/merci");
      } else {
        setStatus("error");
      }
    } catch (err) {
      setStatus("error");
    }
  };

  const inputBase =
    "w-full px-4 py-3 pl-12 text-base border rounded-lg focus:ring-2 focus:ring-[#1C3A27] focus:border-[#1C3A27] transition-colors";
  const errClass = (f) => (errors[f] ? "border-red-400" : "border-[#EDE6DB]");

  return (
    <section
      id="contact-form"
      className="section-padding bg-gradient-to-br from-[#FAF9F6] to-[#EDE6DB]/30"
      aria-labelledby="contact-form-title"
    >
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-10 md:mb-12">
          <h2
            id="contact-form-title"
            className="font-serif text-3xl md:text-4xl lg:text-5xl font-bold text-[#1C3A27] mb-4"
          >
            Vérifier les disponibilités
          </h2>
          <p className="text-lg text-[#1C3A27]/70 max-w-2xl mx-auto leading-relaxed">
            Indiquez-nous vos dates et vos besoins. Nathalie et Wilhelm vous répondront directement.
          </p>
          <p className="mt-3 text-sm text-[#1C3A27]/60 max-w-2xl mx-auto">
            Envoyez-nous votre demande de séjour : nous vérifierons les disponibilités et vous répondrons rapidement. Une demande n'est pas une réservation confirmée.
          </p>
        </div>

        <div className="bg-white rounded-2xl p-6 md:p-8 lg:p-10 shadow-xl border border-[#EDE6DB]/50">
          {status === "error" && (
            <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg flex items-start gap-3">
              <AlertCircle size={20} className="text-red-600 mt-0.5 flex-shrink-0" />
              <p className="text-red-800 text-sm">
                Une erreur est survenue. Vous pouvez réessayer ou nous contacter directement au{" "}
                <a href={`tel:${PHONE}`} className="font-semibold underline">
                  {PHONE_DISPLAY}
                </a>
                .
              </p>
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-8" noValidate>
            {/* Honeypot anti-spam */}
            <input
              type="text"
              name="_honey"
              value={formData._honey}
              onChange={handleChange}
              tabIndex="-1"
              autoComplete="off"
              className="hidden"
              aria-hidden="true"
            />

            {/* Bloc 1 — Votre séjour */}
            <fieldset>
              <legend className="font-serif text-xl font-bold text-[#1C3A27] mb-4">
                Votre séjour
              </legend>
              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <label htmlFor="checkin" className="block text-sm font-semibold text-[#1C3A27] mb-2">
                    Date d'arrivée *
                  </label>
                  <div className="relative">
                    <input
                      type="date"
                      id="checkin"
                      name="checkin"
                      value={formData.checkin}
                      onChange={handleChange}
                      min={todayStr()}
                      className={`${inputBase} ${errClass("checkin")}`}
                    />
                    <CalendarDays size={20} className="absolute left-4 top-3.5 text-[#1C3A27]/50 pointer-events-none" />
                  </div>
                  {errors.checkin && <p className="text-red-600 text-xs mt-1">{errors.checkin}</p>}
                </div>

                <div>
                  <label htmlFor="checkout" className="block text-sm font-semibold text-[#1C3A27] mb-2">
                    Date de départ *
                  </label>
                  <div className="relative">
                    <input
                      type="date"
                      id="checkout"
                      name="checkout"
                      value={formData.checkout}
                      onChange={handleChange}
                      min={formData.checkin || todayStr()}
                      className={`${inputBase} ${errClass("checkout")}`}
                    />
                    <CalendarDays size={20} className="absolute left-4 top-3.5 text-[#1C3A27]/50 pointer-events-none" />
                  </div>
                  {errors.checkout && <p className="text-red-600 text-xs mt-1">{errors.checkout}</p>}
                </div>
              </div>

              <div className="grid md:grid-cols-2 gap-6 mt-6">
                <div>
                  <label htmlFor="guests" className="block text-sm font-semibold text-[#1C3A27] mb-2">
                    Nombre de voyageurs *
                  </label>
                  <div className="relative">
                    <select
                      id="guests"
                      name="guests"
                      value={formData.guests}
                      onChange={handleChange}
                      className={`${inputBase} appearance-none bg-white ${errClass("guests")}`}
                    >
                      <option value="">Sélectionnez</option>
                      {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map((n) => (
                        <option key={n} value={n}>
                          {n} {n > 1 ? "voyageurs" : "voyageur"}
                        </option>
                      ))}
                    </select>
                    <Users size={20} className="absolute left-4 top-3.5 text-[#1C3A27]/50 pointer-events-none" />
                  </div>
                  {errors.guests && <p className="text-red-600 text-xs mt-1">{errors.guests}</p>}
                </div>

                <div>
                  <label className="block text-sm font-semibold text-[#1C3A27] mb-2">
                    Voyagez-vous avec un animal ?
                  </label>
                  <div className="flex gap-3 items-center">
                    <div className="relative flex-1">
                      <select
                        name="pet"
                        value={formData.pet}
                        onChange={handleChange}
                        className={`${inputBase} appearance-none bg-white border-[#EDE6DB]`}
                        aria-label="Voyagez-vous avec un animal"
                      >
                        <option value="non">Non</option>
                        <option value="oui">Oui</option>
                      </select>
                      <PawPrint size={20} className="absolute left-4 top-3.5 text-[#1C3A27]/50 pointer-events-none" />
                    </div>
                    {formData.pet === "oui" && (
                      <input
                        type="number"
                        name="petCount"
                        min="1"
                        max="5"
                        value={formData.petCount}
                        onChange={handleChange}
                        placeholder="Combien ?"
                        className="w-28 px-3 py-3 text-base border border-[#EDE6DB] rounded-lg focus:ring-2 focus:ring-[#1C3A27] focus:border-[#1C3A27]"
                        aria-label="Nombre d'animaux"
                      />
                    )}
                  </div>
                </div>
              </div>
            </fieldset>

            {/* Bloc 2 — Options */}
            <fieldset>
              <legend className="font-serif text-xl font-bold text-[#1C3A27] mb-4">Options</legend>
              <div className="space-y-3">
                <label className="flex items-start gap-3 p-4 rounded-lg border border-[#EDE6DB] hover:bg-[#EDE6DB]/20 cursor-pointer transition-colors">
                  <input
                    type="checkbox"
                    name="towels"
                    checked={formData.towels}
                    onChange={handleChange}
                    className="mt-1 w-5 h-5 accent-[#1C3A27] flex-shrink-0"
                  />
                  <span className="flex items-center gap-2 flex-wrap">
                    <Sparkles size={18} className="text-[#1C3A27]" />
                    <span className="font-medium text-[#1C3A27]">Je souhaite réserver les serviettes</span>
                    <span className="block w-full text-sm text-[#1C3A27]/60">
                      2 serviettes/personne — 6 €/personne pour le séjour
                      {towelEstimate > 0 && (
                        <span className="ml-1 font-semibold text-[#1C3A27]">
                          • Estimé : {towelEstimate} €
                        </span>
                      )}
                    </span>
                  </span>
                </label>

                <label className="flex items-start gap-3 p-4 rounded-lg border border-[#EDE6DB] hover:bg-[#EDE6DB]/20 cursor-pointer transition-colors">
                  <input
                    type="checkbox"
                    name="babyCot"
                    checked={formData.babyCot}
                    onChange={handleChange}
                    className="mt-1 w-5 h-5 accent-[#1C3A27] flex-shrink-0"
                  />
                  <span className="flex items-center gap-2">
                    <Baby size={18} className="text-[#1C3A27]" />
                    <span className="font-medium text-[#1C3A27]">J'aurai besoin d'un lit bébé</span>
                  </span>
                </label>

                <label className="flex items-start gap-3 p-4 rounded-lg border border-[#EDE6DB] hover:bg-[#EDE6DB]/20 cursor-pointer transition-colors">
                  <input
                    type="checkbox"
                    name="babyChair"
                    checked={formData.babyChair}
                    onChange={handleChange}
                    className="mt-1 w-5 h-5 accent-[#1C3A27] flex-shrink-0"
                  />
                  <span className="flex items-center gap-2">
                    <Baby size={18} className="text-[#1C3A27]" />
                    <span className="font-medium text-[#1C3A27]">J'aurai besoin d'une chaise haute</span>
                  </span>
                </label>
              </div>

              {/* Encart réassurance */}
              <div className="mt-4 flex items-start gap-2 text-sm text-[#1C3A27]/70 bg-[#EDE6DB]/30 rounded-lg p-3">
                <Info size={16} className="mt-0.5 flex-shrink-0" />
                <span>
                  Draps inclus • Lits faits à l'arrivée • Séjour minimum 2 nuits • Ménage obligatoire : 70 €
                </span>
              </div>
            </fieldset>

            {/* Bloc 3 — Vos coordonnées */}
            <fieldset>
              <legend className="font-serif text-xl font-bold text-[#1C3A27] mb-4">Vos coordonnées</legend>
              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <label htmlFor="name" className="block text-sm font-semibold text-[#1C3A27] mb-2">
                    Nom &amp; prénom *
                  </label>
                  <div className="relative">
                    <input
                      type="text"
                      id="name"
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      className={`${inputBase} ${errClass("name")}`}
                      placeholder="Votre nom complet"
                    />
                    <Users size={20} className="absolute left-4 top-3.5 text-[#1C3A27]/50 pointer-events-none" />
                  </div>
                  {errors.name && <p className="text-red-600 text-xs mt-1">{errors.name}</p>}
                </div>

                <div>
                  <label htmlFor="email" className="block text-sm font-semibold text-[#1C3A27] mb-2">
                    E-mail *
                  </label>
                  <div className="relative">
                    <input
                      type="email"
                      id="email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      className={`${inputBase} ${errClass("email")}`}
                      placeholder="votre@email.fr"
                    />
                    <Mail size={20} className="absolute left-4 top-3.5 text-[#1C3A27]/50 pointer-events-none" />
                  </div>
                  {errors.email && <p className="text-red-600 text-xs mt-1">{errors.email}</p>}
                </div>
              </div>

              <div className="mt-6">
                <label htmlFor="phone" className="block text-sm font-semibold text-[#1C3A27] mb-2">
                  Téléphone
                </label>
                <div className="relative">
                  <input
                    type="tel"
                    inputMode="tel"
                    id="phone"
                    name="phone"
                    value={formData.phone}
                    onChange={handleChange}
                    className={`${inputBase} border-[#EDE6DB]`}
                    placeholder="06 12 34 56 78"
                  />
                  <Phone size={20} className="absolute left-4 top-3.5 text-[#1C3A27]/50 pointer-events-none" />
                </div>
              </div>

              <div className="mt-6">
                <label htmlFor="message" className="block text-sm font-semibold text-[#1C3A27] mb-2">
                  Votre message
                </label>
                <div className="relative">
                  <textarea
                    id="message"
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    rows={4}
                    className="w-full px-4 py-3 pl-12 text-base border border-[#EDE6DB] rounded-lg focus:ring-2 focus:ring-[#1C3A27] focus:border-[#1C3A27] transition-colors resize-none"
                    placeholder="Une question, une demande particulière ou quelque chose que nous devrions savoir ?"
                  />
                  <MessageSquare size={20} className="absolute left-4 top-3.5 text-[#1C3A27]/50 pointer-events-none" />
                </div>
              </div>
            </fieldset>

            {/* Récapitulatif dynamique */}
            {formData.checkin && formData.checkout && formData.guests && (
              <div className="bg-[#1C3A27]/5 border border-[#1C3A27]/10 rounded-xl p-5">
                <h3 className="text-sm font-semibold text-[#1C3A27] mb-3">Récapitulatif de votre demande</h3>
                <ul className="text-sm text-[#1C3A27]/80 space-y-1">
                  <li>
                    <strong>Du {formatDate(formData.checkin)} au {formatDate(formData.checkout)}</strong>
                    {nights >= 2 && <span> • {nights} nuits</span>}
                  </li>
                  <li>{formData.guests} voyageur{Number(formData.guests) > 1 ? "s" : ""}</li>
                  <li>Animal : {formData.pet === "oui" ? "oui" : "non"}</li>
                  <li>
                    Serviettes : {formData.towels ? `oui${towelEstimate ? ` (≈ ${towelEstimate} €)` : ""}` : "non"}
                  </li>
                  {(formData.babyCot || formData.babyChair) && (
                    <li>
                      Équipement bébé :{" "}
                      {[formData.babyCot && "lit bébé", formData.babyChair && "chaise haute"]
                        .filter(Boolean)
                        .join(", ")}
                    </li>
                  )}
                </ul>
              </div>
            )}

            {/* CTA */}
            <div className="text-center pt-2">
              <button
                type="submit"
                disabled={status === "sending"}
                className="btn-primary text-lg px-8 py-4 min-w-[240px] disabled:opacity-60 disabled:cursor-not-allowed"
              >
                {status === "sending" ? (
                  <span className="flex items-center justify-center space-x-2">
                    <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                    <span>Envoi de votre demande…</span>
                  </span>
                ) : (
                  <span className="flex items-center justify-center space-x-2">
                    <Send size={20} />
                    <span>Envoyer ma demande de séjour</span>
                  </span>
                )}
              </button>
              <p className="text-xs text-[#1C3A27]/60 mt-3">
                Vous ne payez rien maintenant — il s'agit d'une simple demande de disponibilité.
              </p>
            </div>
          </form>
        </div>

        {/* Plateformes partenaires */}
        <div className="mt-12 text-center">
          <p className="text-[#1C3A27]/70 mb-6">Ou réservez directement sur nos plateformes partenaires :</p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
            <a
              href="https://www.airbnb.fr/rooms/1124426465764074190"
              target="_blank"
              rel="noopener noreferrer"
              className="btn-secondary px-8 py-3 w-full sm:w-auto"
              aria-label="Réserver sur Airbnb - ouverture dans un nouvel onglet"
            >
              Réserver sur Airbnb
            </a>
            <a
              href="https://www.booking.com/hotel/fr/gite-au-coeur-de-la-suisse-normande.fr.html"
              target="_blank"
              rel="noopener noreferrer"
              className="btn-secondary px-8 py-3 w-full sm:w-auto"
              aria-label="Réserver sur Booking - ouverture dans un nouvel onglet"
            >
              Réserver sur Booking
            </a>
            <a
              href={`tel:${PHONE}`}
              className="btn-secondary px-8 py-3 w-full sm:w-auto"
              aria-label={`Appeler le ${PHONE_DISPLAY}`}
            >
              Appeler
            </a>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ContactFormSection;
